/**
 * "AI needs credit", in place (TMMAPP-0113 §C 6, as amended by TMMAPI-0151): what an AI action the
 * person started shows when tmm-api refuses it `409 AI_DISABLED` for a wallet reason — shared like
 * `SubscribeInPlace`. The SPA never starts AI it can't pay for; it shows the server's refusal
 * (MM-D-131):
 * - `WALLET_EMPTY` + `firstPurchaseRequired` → "AI is prepaid…" and ENABLE AI (the first purchase);
 * - `WALLET_EMPTY` → "Your AI balance is {available}…" and TOP UP;
 * - `NO_PRICE` → "AI isn't available in your currency yet." — no action;
 * - `PERSON_CAP` → "You've reached your monthly AI limit of {cap}." and a link to the limit
 *   (Settings → Automation); no purchase is offered;
 * - `AI_DEBT` (TMMAPI-0152, MM-D-133) → "Your AI balance owes {debt}…" and TOP UP (a top-up pays the
 *   debt first);
 * - `BUSY` → "AI is busy right now…" and Try again; no purchase;
 * - `SUBSCRIPTION_INACTIVE` → "Renew your subscription to use AI." and Renew (Settings →
 *   Subscription); no top-up.
 * ENABLE AI shows "{amount} plus applicable tax" before the person confirms (TMMAPP-0119 item 9),
 * and who bills it ("Billed by …", §A3).
 * Only the wallet's owner reaches this: every AI action is the owner's (TMMAPI-0146; TMMAPP-0114 A2),
 * and a member reads a failed job's reason without it (`AiJobFailure`).
 * After a purchase or top-up it re-reads the wallet and offers Try again: nothing reruns on its own.
 * An embedded suggestion never renders this (it stays silent).
 * Inside a modal (`swap`), TOP UP swaps the host's whole body (title, content, actions) for the
 * top-up and returns to it after (DLG-005, TMMAPP-0118 C11); on a page it opens the top-up dialog.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Alert, Box, Button, Stack } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { aiWalletApi, type AiWallet } from '@/api/aiWallet';
import type { AiRefusal, AiWalletReason } from '@/api/errors';
import { settingsHref } from '@/features/settings/settingsHref';
import { formatMoneyMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { SubmitButton } from '@/components/SubmitButton';
import { SellerLine } from '@/features/billing/SellerLine';
import { ChargeOutcomeNotice } from './ChargeOutcomeNotice';
import { chargePreview } from './chargePreview';
import { TopUpDialog, TopUpPanel } from './TopUpDialog';
import { chargeInFlight, topUpStaysOpen, useAiWalletCharge } from './useAiWalletCharge';
import type { TopUpSwap } from './useTopUpSwap';

interface AiWalletPromptProps {
  refusal: AiRefusal & { reason: AiWalletReason };
  /** The host's own action, run again only when the person presses Try again. */
  onTryAgain: () => void;
  /**
   * The modal this prompt sits in: the in-place subscribe card closes it before the subscribe dialog
   * opens (DLG-005, TMMAPP-0117 B6). Every modal host passes it; a page has nothing to close.
   */
  onCloseHost?: (() => void) | undefined;
  /** The modal host's body swap (`useTopUpSwap`): TOP UP replaces the host's body in place. */
  swap?: TopUpSwap | undefined;
  /** A purchase or top-up was credited: the host re-reads what it gates on (nothing reruns). */
  onCharged?: (() => void) | undefined;
  /**
   * When false, an `AI_DEBT` amount is only `refusal.debtMinor`. A failed job must not substitute
   * the meter's debt (TMMAPP-0129). The synchronous refusal still reads the meter.
   */
  meterDebt?: boolean | undefined;
}

export function AiWalletPrompt({
  refusal,
  onTryAgain,
  onCloseHost,
  swap,
  onCharged,
  meterDebt = true,
}: AiWalletPromptProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const [wallet, setWallet] = useState<AiWallet | null>(null);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const topUpRef = useRef<HTMLButtonElement | null>(null);
  const noticeRef = useRef<HTMLDivElement | null>(null);
  const charge = useAiWalletCharge({ wallet, onWallet: setWallet });
  const needsWallet =
    refusal.reason !== 'NO_PRICE' &&
    refusal.reason !== 'BUSY' &&
    refusal.reason !== 'SUBSCRIPTION_INACTIVE';

  // The figures the refusal doesn't carry (currency, the first purchase, the recharge amount, the
  // limit) come from the meter.
  useEffect(() => {
    if (!needsWallet) return undefined;
    let live = true;
    aiWalletApi
      .get()
      .then((next) => {
        if (live) setWallet(next);
      })
      .catch(() => {
        /* the copy falls back to its plain form */
      });
    return () => {
      live = false;
    };
  }, [needsWallet]);

  const currency = wallet?.currency ?? null;
  const money = (minor: number | null) =>
    minor === null || currency === null ? null : formatMoneyMinor(minor, currency, locale);
  const { outcome } = charge;
  const busy = outcome.kind === 'busy';
  const inFlight = chargeInFlight(outcome);
  const done = outcome.kind === 'added';
  const canTopUp = wallet !== null;
  const topUpShown = topUpOpen && topUpStaysOpen(outcome) && canTopUp;
  // In a modal the top-up replaces the host's body; on a page it is a dialog of its own.
  const panelOpen = swap !== undefined && topUpShown;
  const onSwap = swap?.onSwap;

  const onChargedRef = useRef(onCharged);
  useEffect(() => {
    onChargedRef.current = onCharged;
  });
  useEffect(() => {
    if (done) onChargedRef.current?.();
  }, [done]);

  useEffect(() => {
    if (!panelOpen || onSwap === undefined) return undefined;
    onSwap(true);
    return () => onSwap(false);
  }, [panelOpen, onSwap]);

  // Back from the panel: Cancel returns focus to TOP UP; an answer the prompt shows takes it. The
  // host shows its body again on its next render, so focus waits two frames for it.
  const wasPanelOpen = useRef(false);
  useEffect(() => {
    if (wasPanelOpen.current && !panelOpen) {
      const target = topUpOpen ? noticeRef : topUpRef;
      let frame = window.requestAnimationFrame(() => {
        frame = window.requestAnimationFrame(() => target.current?.focus());
      });
      wasPanelOpen.current = panelOpen;
      return () => window.cancelAnimationFrame(frame);
    }
    wasPanelOpen.current = panelOpen;
    return undefined;
  }, [panelOpen, topUpOpen]);

  const topUpButton =
    wallet !== null ? (
      <Button
        ref={topUpRef}
        color="inherit"
        size="small"
        disabled={inFlight}
        onClick={() => {
          charge.reset();
          setTopUpOpen(true);
        }}
      >
        {t('aiWallet.topUp')}
      </Button>
    ) : null;

  let text: string;
  let detail: string | null = null;
  let action: ReactNode = null;
  // ENABLE AI (the first purchase) is offered: the card is charged from this prompt.
  let firstPurchase = false;
  if (refusal.reason === 'NO_PRICE') {
    text = t('aiWallet.prompt.noPrice');
  } else if (refusal.reason === 'BUSY') {
    text = t('ai.reason.BUSY');
    action = (
      <Button color="inherit" size="small" onClick={onTryAgain}>
        {t('aiWallet.tryAgain')}
      </Button>
    );
  } else if (refusal.reason === 'SUBSCRIPTION_INACTIVE') {
    text = t('ai.reason.SUBSCRIPTION_INACTIVE');
    // The section's own label (the Subscription section renews), never a hard-coded name.
    action = (
      <Button component={RouterLink} to={settingsHref('subscription')} color="inherit" size="small">
        {t('plan.renew')}
      </Button>
    );
  } else if (refusal.reason === 'AI_DEBT') {
    const debt = money(refusal.debtMinor ?? (meterDebt ? (wallet?.debtMinor ?? null) : null));
    text = debt !== null ? t('aiWallet.prompt.debt', { debt }) : t('ai.reason.AI_DEBT');
    action = topUpButton;
  } else if (refusal.reason === 'PERSON_CAP') {
    const cap = money(wallet?.personCapMinor ?? null);
    text =
      cap !== null ? t('aiWallet.prompt.personCap', { cap }) : t('aiWallet.prompt.personCapPlain');
    // The section's own label, never a hard-coded tab name (TMMAPP-0121 item 14). The personal
    // limit lives on AI Automation (TMMAPP-0161 item 2).
    action = (
      <Button
        component={RouterLink}
        to={settingsHref('ai-automation')}
        color="inherit"
        size="small"
      >
        {t('aiWallet.prompt.changeLimit')}
      </Button>
    );
  } else if (refusal.firstPurchaseRequired) {
    firstPurchase = true;
    const amount = money(wallet?.firstPurchaseMinor ?? null);
    text =
      amount !== null
        ? t('aiWallet.prompt.firstPurchase', { amount })
        : t('aiWallet.prompt.firstPurchasePlain');
    detail =
      currency === null
        ? null
        : chargePreview(t, wallet?.firstPurchaseMinor ?? null, currency, locale);
    action = (
      <SubmitButton
        color="inherit"
        size="small"
        variant="text"
        loading={busy}
        disabled={inFlight && !busy}
        onClick={() => void charge.purchase()}
      >
        {t('aiWallet.enable')}
      </SubmitButton>
    );
  } else {
    const available = money(refusal.availableMinor ?? wallet?.availableMinor ?? null);
    text =
      available !== null
        ? t('aiWallet.prompt.topUp', { available })
        : t('aiWallet.prompt.topUpPlain');
    action = topUpButton;
  }

  const closePanel = () => {
    setTopUpOpen(false);
    charge.reset();
  };
  const panel =
    panelOpen && swap !== undefined && swap.panelHost !== null && wallet !== null
      ? createPortal(
          // The panel owns Esc and the host's close while it shows (TMMAPP-0119 item 16).
          <TopUpPanel
            currency={wallet.currency}
            minimumMinor={wallet.rechargeAmountMinor}
            outcome={outcome}
            onTopUp={(amountMinor) => void charge.topUp(amountMinor)}
            onRefresh={() => void charge.refresh()}
            onClose={closePanel}
            onCloseHost={() => {
              closePanel();
              onCloseHost?.();
            }}
            titleId={swap.titleId}
            onCloseRequest={swap.onCloseRequest}
          />,
          swap.panelHost,
        )
      : null;

  return (
    <Stack spacing={1} data-ai-wallet-prompt="">
      {!done && (
        <Alert severity="info" role="status" aria-live="polite" action={action}>
          {text}
          {detail !== null && (
            <Box component="span" data-charge-preview="" sx={{ display: 'block' }}>
              {detail}
            </Box>
          )}
          {/* ENABLE AI charges the card here: who bills sits with its preview (TMMAPP-0119 §A3). */}
          {firstPurchase && <SellerLine inAlert />}
        </Alert>
      )}
      {(!topUpOpen || !topUpStaysOpen(outcome)) &&
        outcome.kind !== 'idle' &&
        outcome.kind !== 'busy' && (
          <Box ref={noticeRef} tabIndex={-1}>
            <ChargeOutcomeNotice
              outcome={outcome}
              onRefresh={() => void charge.refresh()}
              onCloseHost={onCloseHost}
              successAction={
                <Button color="inherit" size="small" onClick={onTryAgain}>
                  {t('aiWallet.tryAgain')}
                </Button>
              }
            />
          </Box>
        )}
      {panel}
      {!panelOpen && topUpShown && wallet !== null && (
        <TopUpDialog
          currency={wallet.currency}
          minimumMinor={wallet.rechargeAmountMinor}
          outcome={outcome}
          onTopUp={(amountMinor) => void charge.topUp(amountMinor)}
          onRefresh={() => void charge.refresh()}
          onClose={closePanel}
        />
      )}
    </Stack>
  );
}
